import { fromJS, Map as IMap } from 'immutable';

import { giftCardFieldNames as gcKeys } from '../item/newItemForm';
import { itemHasAccessory, getItemAccessory, getItemCurrency } from '../item/itemSelectors';



export const filterAndMapKeys = (originData, keyMap) => (
    originData.filter((_, key) => keyMap.has(key)).mapKeys(
        originKey => keyMap.get(originKey, originKey)
    )
);


export const paymentKeyMap = fromJS({
    paymentMethod: 'paymentMethod',
    token: 'token',
    orderID: 'orderId',
    response: 'response',
    type: 'creditCardType',
    expYear: 'expirationYear',
    expMonth: 'expirationMonth',
    securityCode: 'securityCode',
    creditCard: 'creditCardNumber',
    payerID: 'payerId',
    firstName: 'purchaserFirstName',
    lastName: 'purchaserLastName',
    street1: 'street',
    street2: 'street2',
    city: 'city',
    state: 'state',
    postalCode: 'zipCode',
    countryCode: 'countryCode',
    phoneNumber: 'phone',
    senderEmail: 'purchaserEmail',
    masterpassOauthToken: 'masterpassOauthToken',
    checkoutId: 'checkoutId',
    cartId: 'cartId'
});

export const metaDataKeyMap = fromJS({
    amount: 'amount',
    currencyCode: 'currencyCode',
    brandCode: 'brandCode',
    loyaltyNumber: 'loyaltyNumber',
    senderOptIn: 'notifyOffers',
    promoCode: 'promoCode',
    locale: 'languageCode',
    discountAmount: 'totalDiscountRewardAmount'
});

export const giftCardKeyMap = fromJS({
    [gcKeys.MESSAGE_FIELD]: 'message',
    [gcKeys.QUANTITY_FIELD]: 'quantity',
    [gcKeys.AMOUNT_FIELD]: 'faceValue',
    [gcKeys.FACEPLATE_FIELD]: 'faceplateCode',
    [gcKeys.RECIPIENT_SEND_DATETIME_FIELD]: 'deliveryDate',
    [gcKeys.SENDER_NAME_FIELD]: 'senderName',
    [gcKeys.RECIPIENT_EMAIL_FIELD]: 'recipientEmail',
    [gcKeys.RECIPIENT_NAME_FIELD]: 'recipientName',
    [gcKeys.RECIPIENT_SMS_FIELD]: 'recipientSmsNumber',
    [gcKeys.IS_SELFBUY_FIELD]: 'selfBuy',
    [gcKeys.UYO_IMAGE_URL]: 'uyoImageUrl',
    [gcKeys.PROGRAM_CODE]: 'programCode'
});

export const shippingRecipientKeyMap = fromJS({
    fullName: 'fullName',
    street1: 'street',
    street2: 'street2',
    city: 'city',
    state: 'state',
    countryCode: 'countryCode',
    postalCode: 'zipCode',
    phoneNumber: 'phone'
});

export const responseKeyMap = fromJS({
    statement: 'statement',
    orderNumber: 'orderNumber',
    billingAddress: 'billingAddressFromOrderResponse'
});


export const sanitizedPaymentKeyMap = fromJS({
    fullName: 'fullName',
    firstName: 'firstName',
    lastName: 'lastName',
    street1: 'street1',
    street2: 'street2',
    city: 'city',
    state: 'state',
    postalCode: 'postalCode',
    countryCode: 'countryCode',
    phoneNumber: 'phoneNumber',
    paymentMethod: 'paymentMethod',
    purchaserEmail: 'purchaserEmail',
    senderEmail: 'senderEmail',
    purchaseDate: 'purchaseDate',
    currency: 'currency'
});



export const stripWhiteListedCharactersFromPhoneNumber = field => field.replace(/[-()+. ]/g, '');



export const paymentSerializer = (payment) => {
    const serializedPaymentData = filterAndMapKeys(payment, paymentKeyMap).asMutable();
    
    if (serializedPaymentData.get('phone')) {
        serializedPaymentData.update('phone', value => stripWhiteListedCharactersFromPhoneNumber(value));
    }
    return serializedPaymentData.asImmutable();
};


export const metaDataSerializer = (metaData) => {
    const serializedMeta = filterAndMapKeys(metaData, metaDataKeyMap);
    
    
    return serializedMeta.update('amount', val => val.toFixed(2));
};

export const accessorySerializer = (accessory, currency) => (
    fromJS({
        code: accessory.get('code'),
        cost: accessory.getIn(['costs', currency, 'cost'])
    })
);



export const giftCardItemSerializer = (item, senderEmail, senderName, fees = {}) => {
    const orderCert = filterAndMapKeys(item, giftCardKeyMap).asMutable();
    const { uyopFeeAmount, plasticActivationFeeAmount, digitalActivationFeeAmount } = fees;

    
    orderCert.set('cartItemToken', item.get(gcKeys.ITEM_ID));

    
    if (item.get(gcKeys.IS_SELFBUY_FIELD)) {
        orderCert.set('recipientEmail', senderEmail);
        orderCert.set('senderName', senderName);
        orderCert.set('recipientName', senderName);
    }

    if (item.get(gcKeys.IS_PLASTIC_FIELD)) {
        
        if (uyopFeeAmount && item.get(gcKeys.UYO_IMAGE_URL)) {
            orderCert.set('uyopFeeAmount', uyopFeeAmount);
        }
        
        if (plasticActivationFeeAmount) {
            orderCert.set('plasticActivationFeeAmount', plasticActivationFeeAmount);
        }
        
        if (!item.get(gcKeys.RECIPIENT_PLASTIC_NOTIFICATION, false)) {
            orderCert.delete('recipientEmail');
        }
    } else if (!item.get(gcKeys.IS_PLASTIC_FIELD) && digitalActivationFeeAmount) {
        orderCert.set('digitalActivationFeeAmount', digitalActivationFeeAmount);
    }

    
    if (orderCert.get('recipientSmsNumber')) {
        orderCert.update('recipientSmsNumber', value => stripWhiteListedCharactersFromPhoneNumber(value));
    }

    if (itemHasAccessory(item)) {
        orderCert.set('accessory', accessorySerializer(getItemAccessory(item), getItemCurrency(item)));
    }

    return orderCert.asImmutable();
};



export const shippingRecipientSerializer = (shippingRecipient) => {
    const shippingAddress = filterAndMapKeys(shippingRecipient, shippingRecipientKeyMap).asMutable();
    if (shippingAddress.get('phone')) {
        shippingAddress.update('phone', value => stripWhiteListedCharactersFromPhoneNumber(value));
    }
    return shippingAddress.asImmutable();
};


export const shippingGroupSerializer = (itemGroup, senderEmail, senderName, fees = {}) => (
    IMap({
        shippingAddress: shippingRecipientSerializer(itemGroup.get('address')),
        certs: itemGroup.get('items').map(item => giftCardItemSerializer(item, senderEmail, senderName,
            fees)),
        shippingAmount: Number(itemGroup.get('shippingCost')).toFixed(2), 
        shippingMethodToken: itemGroup.getIn(['shippingMethod', 'token'])
    })
);


export const egiftCardsSerializer = (items, senderEmail, senderName, fees = {}) => (
    items.filterNot(
        item => item.get(gcKeys.IS_PLASTIC_FIELD)
    ).map(egc => giftCardItemSerializer(egc, senderEmail, senderName, fees))
);


export const shippingGroupsSerializer = (groups, senderEmail, senderName, fees = {}) => (
    groups.map(group => shippingGroupSerializer(group, senderEmail, senderName,
        fees)).toList()
);


export const orderSerializer = (payment, metaData, digitalItems, shippingGroups, fees = {}) => {
    const loyaltyNumber = payment.get('loyaltyNumber', null);
    const paymentData = paymentSerializer(payment);
    const senderEmail = paymentData.get('purchaserEmail');
    const senderName = paymentData.get('purchaserFirstName');
    const paymentMethod = paymentData.get('paymentMethod');
    
    
    
    const addLoyaltyNumberToMetadata = meta => (loyaltyNumber ? meta.set('loyaltyNumber', loyaltyNumber) : meta);

    return metaDataSerializer(metaData.update(addLoyaltyNumberToMetadata))
        .merge({
            egiftCards: egiftCardsSerializer(digitalItems, senderEmail, senderName, fees),
            shippingGroups: shippingGroupsSerializer(shippingGroups, senderEmail, senderName,
                fees)
        })
        .set(paymentMethod, paymentData.delete('paymentMethod'));
};


export const orderResponseSerializer = response => (
    filterAndMapKeys(fromJS(response), responseKeyMap)
);


export const sanitizePaymentData = paymentData => (
    filterAndMapKeys(fromJS(paymentData), sanitizedPaymentKeyMap)
);
